/**
 * LadiesLuxuryHero - Premium Luxury Hero Section for Ladies Hub
 * V8: Interactive ticker with click-to-filter, long-press pause, and analytics
 * Features:
 *   - Gradient title with optional shimmer
 *   - Glass-morphism greeting badge
 *   - Premium ticker bar with fade edges
 *   - Click-to-filter on ticker chips
 *   - Long-press pause (mobile), mouseenter pause (desktop)
 *   - Analytics events for interactions
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import LadiesHubSearch from './LadiesHubSearch';

// CSS Injection ID
const LADIES_LUX_CSS_ID = 'ladies-luxury-css-v8';

// Premium Luxury CSS with all polish features
const LADIES_LUX_CSS = `
:root {
  --lux-rose: #d63384;
  --lux-coral: #ff7a7a;
  --lux-gold: #ffb347;
  --lux-ink: #1f1f1f;
  --lux-muted: #6b6b6b;
  --lux-bg: #fbf7f1;
  --lux-card: rgba(255, 255, 255, 0.72);
  --lux-stroke: rgba(214, 51, 132, 0.14);
}

.luxHeroWrap {
  padding: 18px 16px 16px;
  background: 
    radial-gradient(1200px 600px at 50% 0%, rgba(255, 180, 71, 0.16), transparent 60%),
    radial-gradient(900px 500px at 20% 20%, rgba(214, 51, 132, 0.12), transparent 55%),
    var(--lux-bg);
  position: relative;
}

.luxGreeting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 6px 0 8px;
  font-weight: 800;
  letter-spacing: 0.2px;
  font-size: 15px;
  color: var(--lux-muted);
}

.luxGreetingBadge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(214, 51, 132, 0.12);
  box-shadow: 0 10px 25px rgba(31, 31, 31, 0.06);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Title with optional shimmer */
.luxTitle {
  font-weight: 900;
  font-size: 34px;
  line-height: 1.1;
  text-align: center;
  letter-spacing: 0.5px;
  margin: 0;
  background: linear-gradient(90deg, var(--lux-rose) 0%, var(--lux-coral) 45%, var(--lux-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Subtle shimmer animation */
.luxTitleShimmer {
  background: 
    linear-gradient(
      90deg, 
      var(--lux-rose) 0%, 
      var(--lux-coral) 35%,
      rgba(255, 255, 255, 0.85) 50%, 
      var(--lux-coral) 65%,
      var(--lux-gold) 100%
    );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: luxShimmer 4s ease-in-out infinite;
}

@keyframes luxShimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .luxTitleShimmer { animation: none; }
}

.luxTitleUnderline {
  width: 64px;
  height: 4px;
  border-radius: 999px;
  margin: 8px auto 6px;
  background: linear-gradient(90deg, var(--lux-coral), var(--lux-gold));
  opacity: 0.65;
}

.luxSubtitle {
  margin: 0 auto 12px;
  max-width: 680px;
  text-align: center;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(31, 31, 31, 0.55);
  letter-spacing: 0.1px;
  padding: 0 10px;
}

/* =====================
   TICKER WITH FADE MASKS
   ===================== */
.luxTickerWrap {
  margin: 12px auto 16px;
  max-width: 760px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(214, 51, 132, 0.14);
  box-shadow: 0 12px 28px rgba(31, 31, 31, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  overflow: hidden;
  position: relative;
}

/* Fade mask edges - premium marquee look */
.luxTickerMask {
  position: relative;
  -webkit-mask-image: linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0%, #000 6%, #000 94%, transparent 100%);
}

/* Fallback fade overlays */
.luxTickerFadeLeft,
.luxTickerFadeRight {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 48px;
  pointer-events: none;
  z-index: 3;
}

.luxTickerFadeLeft {
  left: 0;
  background: linear-gradient(90deg, rgba(251, 247, 241, 0.95), rgba(251, 247, 241, 0));
}

.luxTickerFadeRight {
  right: 0;
  background: linear-gradient(270deg, rgba(251, 247, 241, 0.95), rgba(251, 247, 241, 0));
}

/* RTL fade directions */
[dir="rtl"] .luxTickerFadeLeft {
  left: auto;
  right: 0;
  background: linear-gradient(270deg, rgba(251, 247, 241, 1), rgba(251, 247, 241, 0));
}

[dir="rtl"] .luxTickerFadeRight {
  right: auto;
  left: 0;
  background: linear-gradient(90deg, rgba(251, 247, 241, 1), rgba(251, 247, 241, 0));
}

.luxTickerTrack {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 24px;
}

.luxTickerMarquee {
  display: flex;
  align-items: center;
  gap: 10px;
  width: max-content;
  will-change: transform;
}

/* Paused state */
.luxTickerMarquee.paused {
  animation-play-state: paused !important;
}

/* =====================
   CHIPS WITH INTERACTIONS
   ===================== */
.luxChip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  border: 1.5px solid rgba(214, 51, 132, 0.22);
  font-weight: 800;
  font-size: 13px;
  color: rgba(31, 31, 31, 0.88);
  box-shadow: 0 2px 8px rgba(31, 31, 31, 0.08);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  
  /* Mobile-safe interactions */
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease, border-color 0.12s ease;
  -webkit-tap-highlight-color: rgba(214, 51, 132, 0.15);
}

.luxChip:active {
  transform: scale(0.94);
  box-shadow: 0 2px 12px rgba(214, 51, 132, 0.25);
  background: rgba(214, 51, 132, 0.12);
  border-color: rgba(214, 51, 132, 0.35);
}

.luxChip:focus-visible {
  outline: 2px solid rgba(255, 140, 0, 0.45);
  outline-offset: 2px;
}

/* Long-press visual feedback */
.luxChip.pressing {
  transform: scale(0.95);
  box-shadow: 0 8px 18px rgba(214, 51, 132, 0.2);
}

.luxChipIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(214, 51, 132, 0.16), rgba(255, 180, 71, 0.16));
  border: 1px solid rgba(214, 51, 132, 0.12);
}

.luxBackBtn {
  position: absolute;
  top: 16px;
  z-index: 5;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(214, 51, 132, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: transform 0.12s ease, background 0.12s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.luxBackBtn:hover {
  background: rgba(255, 255, 255, 1);
}

.luxBackBtn:active {
  transform: scale(0.95);
}

/* LTR: Back button on left, icon points left */
[dir="ltr"] .luxBackBtn {
  left: 16px;
  right: auto;
}

/* RTL: Back button on right, icon points right (back direction in RTL) */
[dir="rtl"] .luxBackBtn {
  right: 16px;
  left: auto;
}

.luxSearchWrap {
  max-width: 600px;
  margin: 0 auto 10px;
  padding: 0 16px;
}

.luxDivider {
  width: 80px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--lux-coral), transparent);
  margin: 0 auto;
  border-radius: 2px;
}

/* Motion keyframes */
@keyframes luxMarqueeLTR {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes luxMarqueeRTL {
  0%   { transform: translateX(0); }
  100% { transform: translateX(50%); }
}

/* Accessibility: reduce motion */
@media (prefers-reduced-motion: reduce) {
  .luxTickerMarquee { animation: none !important; }
}
`;

// Helper functions
const isArabicLang = (lang) => (lang || '').toLowerCase().startsWith('ar');
const clampText = (s, max = 24) => (s.length > max ? s.slice(0, max - 1) + '…' : s);

// Greeting logic
function getGreeting(lang) {
  const h = new Date().getHours();
  const ar = isArabicLang(lang);
  if (h < 12) return ar ? { text: 'صباح الخير', icon: '🌸☀️' } : { text: 'Good Morning', icon: '🌸☀️' };
  if (h < 17) return ar ? { text: 'نهارك سعيد', icon: '🌸✨' } : { text: 'Have a Lovely Day', icon: '🌸✨' };
  return ar ? { text: 'مساء الخير', icon: '🌸🌙' } : { text: 'Good Evening', icon: '🌸🌙' };
}

// Default ticker items with category keys for filtering
const DEFAULT_TICKER_EN = [
  { icon: '👗', label: 'Dresses & Sets', categoryKey: 'fashion' },
  { icon: '💄', label: 'Makeup Offers', categoryKey: 'beauty' },
  { icon: '💍', label: 'Gold & Jewelry', categoryKey: 'jewellery' },
  { icon: '👜', label: 'Bags & Accessories', categoryKey: 'fashion' },
  { icon: '✨', label: 'Beauty Services', categoryKey: 'services' },
  { icon: '👠', label: "Women's Shoes", categoryKey: 'fashion' },
];

const DEFAULT_TICKER_AR = [
  { icon: '👗', label: 'فساتين وأطقم', categoryKey: 'fashion' },
  { icon: '💄', label: 'عروض مكياج', categoryKey: 'beauty' },
  { icon: '💍', label: 'ذهب ومجوهرات', categoryKey: 'jewellery' },
  { icon: '👜', label: 'شنط وإكسسوارات', categoryKey: 'fashion' },
  { icon: '✨', label: 'خدمات التجميل', categoryKey: 'services' },
  { icon: '👠', label: 'أحذية نسائية', categoryKey: 'fashion' },
];

// Analytics helper
const trackTickerEvent = (eventName, data) => {
  try {
    // Send to analytics (adapt to your analytics system)
    if (window.gtag) {
      window.gtag('event', eventName, data);
    }
    // Also log for debugging
    console.log(`[Analytics] ${eventName}:`, data);
  } catch (e) {
    // Silent fail for analytics
  }
};

export default function LadiesLuxuryHero({
  lang = 'ar',
  onSearch,
  onBack,
  searchQuery = '',
  onSearchChange,
  onTickerChipClick, // NEW: Callback when chip is clicked
  titleEn = 'Ladies Hub',
  titleAr = 'قسم السيدات',
  subtitleEn = 'Your space to showcase your creativity with confidence',
  subtitleAr = 'مساحتكِ الخاصة لعرض إبداعاتكِ بثقة ✨',
  tickerItemsEn = DEFAULT_TICKER_EN,
  tickerItemsAr = DEFAULT_TICKER_AR,
  enableShimmer = true,
}) {
  // State for ticker pause
  const [isPaused, setIsPaused] = useState(false);
  const longPressTimerRef = useRef(null);
  const marqueeRef = useRef(null);

  // Inject CSS once on mount
  useEffect(() => {
    if (document.getElementById(LADIES_LUX_CSS_ID)) return;
    const style = document.createElement('style');
    style.id = LADIES_LUX_CSS_ID;
    style.innerHTML = LADIES_LUX_CSS;
    document.head.appendChild(style);
  }, []);

  const isArabic = isArabicLang(lang);
  const dir = isArabic ? 'rtl' : 'ltr';
  const greeting = useMemo(() => getGreeting(lang), [lang]);

  // Get ticker items with text clamping
  const items = useMemo(() => {
    const raw = isArabic ? tickerItemsAr : tickerItemsEn;
    return raw.map((x) => ({ ...x, label: clampText(x.label, 24) }));
  }, [isArabic, tickerItemsEn, tickerItemsAr]);

  // Duplicate items for seamless loop
  const loopItems = useMemo(() => [...items, ...items], [items]);

  // Animation direction: LTR scrolls right→left, RTL scrolls left→right
  const marqueeStyle = useMemo(() => ({
    animation: `${isArabic ? 'luxMarqueeRTL' : 'luxMarqueeLTR'} 18s linear infinite`,
  }), [isArabic]);

  // Handle chip click
  const handleChipClick = useCallback((item, index) => {
    // Track analytics
    trackTickerEvent('ticker_chip_click', {
      chipKey: item.categoryKey,
      chipLabel: item.label,
      locale: lang,
      direction: dir,
      positionIndex: index % items.length, // Original index (not duplicated)
    });

    // Call parent handler if provided
    if (onTickerChipClick) {
      onTickerChipClick(item.categoryKey, item);
    }
  }, [onTickerChipClick, lang, dir, items.length]);

  // Long-press handlers for mobile pause
  const handleTouchStart = useCallback((e) => {
    longPressTimerRef.current = setTimeout(() => {
      setIsPaused(true);
      trackTickerEvent('ticker_pause', { method: 'longpress', locale: lang });
      // Add haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(10);
    }, 250);
  }, [lang]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsPaused(false);
  }, []);

  // Desktop hover pause (non-blocking)
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true);
    trackTickerEvent('ticker_pause', { method: 'hover', locale: lang });
  }, [lang]);

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  return (
    <section 
      className="luxHeroWrap" 
      dir={dir}
      data-testid="ladies-luxury-hero"
    >
      {/* Back Button - Proper RTL/LTR positioning and icon direction */}
      {onBack && (
        <motion.button
          onClick={onBack}
          className="luxBackBtn"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
          data-testid="ladies-hero-back"
          aria-label={isArabic ? 'رجوع' : 'Go back'}
        >
          {/* In RTL (Arabic), back = right arrow (going back to previous screen which is to the right)
              In LTR (English), back = left arrow (going back to previous screen which is to the left) */}
          {isArabic ? (
            <ArrowRight size={20} color="#d63384" strokeWidth={2.5} />
          ) : (
            <ArrowLeft size={20} color="#d63384" strokeWidth={2.5} />
          )}
        </motion.button>
      )}

      {/* Greeting Badge */}
      <motion.div
        className="luxGreeting"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="luxGreetingBadge">
          <span aria-hidden="true">{greeting.icon}</span>
          <span>{greeting.text}</span>
        </div>
      </motion.div>

      {/* Gradient Title with Optional Shimmer */}
      <motion.h1
        className={`luxTitle ${enableShimmer ? 'luxTitleShimmer' : ''}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {isArabic ? titleAr : titleEn}
      </motion.h1>

      {/* Title Underline Accent */}
      <motion.div
        className="luxTitleUnderline"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      />

      {/* Subtitle */}
      <motion.p
        className="luxSubtitle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.35 }}
      >
        {isArabic ? subtitleAr : subtitleEn}
      </motion.p>

      {/* Search Bar */}
      <motion.div
        className="luxSearchWrap"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <LadiesHubSearch
          value={searchQuery}
          onChange={onSearchChange}
          onSubmit={onSearch}
          lang={lang}
        />
      </motion.div>

      {/* Premium Ticker Bar with Click-to-Filter */}
      <motion.div
        className="luxTickerWrap"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        aria-label={isArabic ? 'الأكثر رواجاً' : 'Trending'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Fade overlays for premium edge effect */}
        <div className="luxTickerFadeLeft" />
        <div className="luxTickerFadeRight" />
        
        <div className="luxTickerMask">
          <div className="luxTickerTrack">
            <div 
              ref={marqueeRef}
              className={`luxTickerMarquee ${isPaused ? 'paused' : ''}`} 
              style={marqueeStyle}
            >
              {loopItems.map((item, idx) => (
                <span 
                  key={idx} 
                  className="luxChip"
                  tabIndex={0}
                  role="button"
                  aria-label={`${isArabic ? 'تصفية حسب' : 'Filter by'}: ${item.label}`}
                  onClick={() => handleChipClick(item, idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleChipClick(item, idx);
                    }
                  }}
                  data-testid={`ticker-chip-${item.categoryKey}`}
                >
                  <span className="luxChipIcon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bottom Divider */}
      <motion.div
        className="luxDivider"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      />
    </section>
  );
}
